/* eslint-disable no-shadow */

import Vue from 'vue';

import date from '@/mixins/date';

import Common from '@/store/common';
import Store from '@/store/index';

const state = {
  surveys: [],
  responses: [],
  activeSurvey: 0,
  activeResponse: 0,
  showArchivedSurveys: false,
  showArchivedSurveyResponses: false,
  surveySchema: {
    basicInfo: {
      fields: [
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Title',
          model: 'title',
          validator: 'string',
          required: true,
        },
        {
          type: 'customSelect',
          styleClasses: 'form__field',
          label: 'Branding',
          model: 'branding',
          selectOptions: {
            hideNoneSelectedText: true
          },
          values: () => {
            return [
              { id: 'rradar', value: 'rradar', name: 'rradar' },
              { id: 'rrelentless', value: 'rrelentless', name: 'rrelentless' },
            ]
          }
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Notification Emails',
          hint: 'Comma seperated list of email addresses to send notification of responses',
          model: 'notification_emails',
          validator: 'string',
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Email Template',
          hint: 'If the survey has an email address field, send an email using this Mandrill template',
          model: 'email_template',
          validator: 'string',
        },
        {
          type: 'customCheckbox',
          styleClasses: 'form__field',
          label: 'Requires Login?',
          model: 'requires_user',
        },
        {
          type: 'customCheckbox',
          styleClasses: 'form__field',
          label: 'Published',
          model: 'published',
          onlyOn: 'edit',
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Auto Invite Frequency',
          hint: 'How many weeks until users get invited to submit a new response',
          model: 'auto_invite_frequency',
          validator: 'number',
          visible: (model) => { return model && model.requires_user; },
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Content Code',
          hint: 'Restrict access to certain users. Code friendly name of package content e.g. CYBER_ESSENTIALS',
          model: 'content_code',
          validator: 'string',
          visible: (model) => { return model && model.requires_user; },
        },
        {
          type: 'customChecklist',
          styleClasses: 'form__field',
          label: 'Who has admin access to this survey? (leave all unticked to give everyone access)',
          model: 'access_user_ids',
          values: () => {
            return Store.state.admin.user.admins.map((admin) => {
              return { id: admin.id, value: admin.id, name: `${admin.first_name} ${admin.last_name}` };
            }).sort((a, b) => a.name.localeCompare(b.name));
          },
          listBox: true,
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Add Survey',
          onlyOn: 'new',
          onSubmit: (model, schema, context, form) => {
            context.addSurvey(model, form);
          },
          validateBeforeSubmit: true,
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Save Survey',
          onlyOn: 'edit',
          onSubmit: (model, schema, context, form) => {
            context.editForm(model, form);
          },
          validateBeforeSubmit: true,
        },
      ],
    },
  },
};

const getters = {
  orderedSurveys: (state) => {
    return state.surveys.concat()
      .sort((a, b) => date.methods.parseDateTime(b.updated_at) - date.methods.parseDateTime(a.updated_at))
      .filter(survey => state.showArchivedSurveys || !survey.archived);
  },
  activeSurvey: state => state.surveys.find(survey => survey.id === state.activeSurvey) || {},
  activeResponse: state => state.responses.find(response => response.id === state.activeResponse) || {},
  activeSurveyId: (state, getters) => {
    const surveyId = state.surveys.findIndex(survey => survey.id === getters.activeSurvey.id);
    return surveyId >= 0 ? surveyId : 0;
  },
  surveyIndexById: state => (surveyId) => {
    const surveyIndex = state.surveys.findIndex(survey => survey.id === surveyId);
    return surveyIndex >= 0 ? surveyIndex : 0;
  },
  surveyResponseIndexById: state => (surveyResponseId) => {
    const surveyResponseIndex = state.responses.findIndex(response => response.id === surveyResponseId);
    return surveyResponseIndex >= 0 ? surveyResponseIndex : 0;
  },
  aggregateResponse: (state, getters) => getters.activeSurvey.aggregateResponse,
};

const mutations = {
  setSurveys(state, surveys) {
    state.surveys = surveys;
  },

  setResponses(state, responses) {
    state.responses = responses;
  },

  setAggregateResponse(state, { surveyId, response }) {
    Vue.set(state.surveys[surveyId], 'aggregateResponse', response.pages);
  },

  setActiveSurvey(state, survey) {
    state.activeSurvey = survey;
  },

  setActiveResponse(state, response) {
    state.activeResponse = response;
  },

  addSurvey(state, survey) {
    state.surveys.push(survey);
  },

  editSurvey(state, { surveyId, editedSurvey }) {
    Vue.set(state.surveys, surveyId, editedSurvey);
  },

  archiveSurvey(state, { surveyId }) {
    Vue.set(state.surveys[surveyId], 'archived', true);
  },

  setShowArchivedSurveys(state, status) {
    state.showArchivedSurveys = status;
  },

  archiveSurveyResponse(state, { surveyResponseId }) {
    Vue.set(state.responses[surveyResponseId], 'archived', true);
  },

  setShowArchivedSurveyResponses(state, status) {
    state.showArchivedSurveyResponses = status;
  },

  setCloningState(state, { status, surveyId }) {
    Vue.set(state.surveys[surveyId], 'cloning', status);
  },
};

const actions = {
  fetchSurveys({ state, commit }) {
    if (state.surveys.length > 0) return false;
    return Vue.axios.get('admin/surveys')
      .then((response) => {
        commit('setSurveys', response.data.survey);
      })
      .catch();
  },

  fetchResponses({ state, getters, commit }, survey) {
    return Vue.axios.get(`admin/surveys/${survey}/responses`)
      .then((response) => {
        commit('setResponses', response.data.response);
      })
      .catch();
  },

  fetchAggregateResponse({ state, getters, commit }, survey) {
    return Vue.axios.get(`admin/surveys/${survey}/aggregate-response`)
      .then((response) => {
        const surveyId = getters.surveyIndexById(survey);
        commit('setAggregateResponse', { surveyId, response: response.data.response });
      })
      .catch();
  },

  addSurvey({ state, commit, dispatch }, { data, form }) {
    return Vue.axios.post('admin/surveys', data)
      .then((response) => {
        commit('addSurvey', response.data.survey);
        dispatch('setMessage', { text: 'Successfully created survey' }, { root: true });
      })
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  editSurvey({ getters, commit, dispatch }, { data, form }) {
    commit('editSurvey', { surveyId: getters.activeSurveyId, editedSurvey: data });
    return Vue.axios.put(`admin/surveys/${data.id}`, data)
      .then(() => dispatch('setMessage', { text: `Successfully edited ${data.title}` }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  archiveSurvey({ state, getters, commit, dispatch }, { surveyId }) {
    commit('archiveSurvey', { surveyId: getters.surveyIndexById(surveyId) });
    return Vue.axios.delete(`admin/surveys/${surveyId}`)
      .then(() => dispatch('setMessage', { text: 'Successfully archived survey' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response));
  },

  archiveSurveyResponse({ state, getters, commit, dispatch }, { surveyResponseId }) {
    commit('archiveSurveyResponse', { surveyResponseId: getters.surveyResponseIndexById(surveyResponseId) });
    return Vue.axios.delete(`admin/survey-responses/${surveyResponseId}`)
      .then(() => dispatch('setMessage', { text: 'Successfully archived survey response' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response));
  },

  cloneSurvey({ getters, commit, dispatch }, { surveyId, clonedTitle }) {
    commit('setCloningState', { status: true, surveyId: getters.surveyIndexById(surveyId) });
    return Vue.axios.post(`admin/surveys/${surveyId}/clone`, { title: clonedTitle })
      .then((response) => {
        commit('addSurvey', response.data.survey);
        commit('setCloningState', { status: false, surveyId: getters.surveyIndexById(surveyId) });
        dispatch('setMessage', { text: 'Successfully cloned survey' }, { root: true });
      }).catch(error => Common.handleError(error));
  },

  addSurveyInvite({ state, getters, commit, dispatch }, { surveyId, actorUid }) {
    return Vue.axios.post('admin/survey-invites', { survey_id: surveyId, actor_uid: actorUid })
      .then((response) => {
        dispatch('setMessage', { text: 'Successfully sent survey invite' }, { root: true });
        return response.data.survey;
      })
      .catch(error => Common.handleError(dispatch, error.response));
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
